import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InvestmentData } from './Investment.interface';

interface initialStateType {
  ar: InvestmentData;
  en: InvestmentData;
  currentTab: number;
}

const initialState: initialStateType = {
  ar: {
    headerSection: {
      investmentTitle: '',
      location: '',
      image: '',
      price: '',
      city: '',
    },
    infoSection: { items: [''] },
    overviewSection: { title: '', paragraphs: [''] },
    detailsSection: {
      title: '',
      plan: [{ key: '', value: '' }],
      paragraphs: [''],
    },
    forInvestmentSection: { title: '', info: [''] },
    imagesGallerySection: { images: [''] },
    locationSection: {
      locationLat: '',
      locationLong: '',
      paragraphs: [''],
      nearbyLocations: [
        { title: '', items: [{ locationName: '', neededTime: '' }] },
      ],
    },
    whyInvestSection: { title: '', image: '', list: [''] },
    footerAdvertisementSection: { image: '', text: '' },
  },
  en: {
    headerSection: {
      investmentTitle: '',
      location: '',
      image: '',
      price: '',
      city: '',
    },
    infoSection: { items: [''] },
    overviewSection: { title: '', paragraphs: [''] },
    detailsSection: {
      title: '',
      plan: [{ key: '', value: '' }],
      paragraphs: [''],
    },
    forInvestmentSection: { title: '', info: [''] },
    imagesGallerySection: { images: [''] },
    locationSection: {
      locationLat: '',
      locationLong: '',
      paragraphs: [''],
      nearbyLocations: [
        { title: '', items: [{ locationName: '', neededTime: '' }] },
      ],
    },
    whyInvestSection: { title: '', image: '', list: [''] },
    footerAdvertisementSection: { image: '', text: '' },
  },
  currentTab: 0,
};

const editInvestmentSlice = createSlice({
  name: 'editProject',
  initialState,
  reducers: {
    setEditInvestmentData: (
      state,
      action: PayloadAction<{
        investmentData: initialStateType;
      }>,
    ) => {
      const { investmentData } = action.payload;
      // state[language] = investmentData; // Update project data for the specific language
      state.ar = investmentData.ar;
      state.en = investmentData.en;
    },
    clearEditInvestmentData: (state) => {
      state.ar = initialState.ar;
      state.en = initialState.en;
      state.currentTab = initialState.currentTab;
    },
    updateFieldValue: (
      state,
      action: PayloadAction<{
        language: 'ar' | 'en';
        section: string;
        field: string;
        value: any;
        isSameValue: boolean;
      }>,
    ) => {
      const { language, section, field, value, isSameValue } = action.payload;
      state[language][section][field] = value; // Update a field in a specific language
      if (isSameValue) {
        state['ar'][section][field] = value;
        state['en'][section][field] = value;
      }
    },
    addItemToArray: (
      state,
      action: PayloadAction<{
        section: string;
        field: string;
        value?: any;
      }>,
    ) => {
      const { section, field, value } = action.payload;
      state['ar'][section][field].push(value ? value : '');
      state['en'][section][field].push(value ? value : '');
    },
    removeItemFromArray: (
      state,
      action: PayloadAction<{
        section: string;
        field: string;
        index: number;
      }>,
    ) => {
      const { section, field, index } = action.payload;
      state['ar'][section][field].splice(index, 1);
      state['en'][section][field].splice(index, 1);
    },
    updateListValue: (
      state,
      action: PayloadAction<{
        language: 'ar' | 'en';
        section: string;
        field: string;
        index: number;
        value: any;
        isSameValue: boolean;
      }>,
    ) => {
      const { language, section, field, index, value, isSameValue } =
        action.payload;
      state[language][section][field][index] = value; // Update list value in a specific language
      if (isSameValue) {
        state['ar'][section][field][index] = value;
        state['en'][section][field][index] = value;
      }
    },
    updateObjectArrayValue: (
      state,
      action: PayloadAction<{
        language: 'ar' | 'en';
        section: string;
        field: string;
        index: number;
        key: string;
        value: any;
        isSameValue: boolean;
      }>,
    ) => {
      const { language, section, field, index, key, value, isSameValue } =
        action.payload;
      state[language][section][field][index][key] = value; // Update object array value in a specific language
      if (isSameValue) {
        state['ar'][section][field][index][key] = value;
        state['en'][section][field][index][key] = value;
      }
    },
    handleNearbyLocations: (
      state,
      action: PayloadAction<{
        language: 'ar' | 'en';
        actionType:
          | 'AddMethod'
          | 'AddMethodName'
          | 'AddLocation'
          | 'AddLocationTime'
          | 'AddLocationName'
          | 'RemoveLocation'
          | 'RemoveMethod';
        index?: number;
        locationIndex?: number;
        value?: any;
      }>,
    ) => {
      const { language, actionType, index, locationIndex, value } =
        action.payload;
      switch (actionType) {
        case 'AddMethod':
          state['ar'].locationSection.nearbyLocations.push({
            title: '',
            items: [{ locationName: '', neededTime: '' }],
          });
          state['en'].locationSection.nearbyLocations.push({
            title: '',
            items: [{ locationName: '', neededTime: '' }],
          });
          break;

        case 'AddMethodName':
          state[language].locationSection.nearbyLocations[index].title = value;
          break;

        case 'AddLocation':
          state['ar'].locationSection.nearbyLocations[index].items.push({
            locationName: '',
            neededTime: '',
          });
          state['en'].locationSection.nearbyLocations[index].items.push({
            locationName: '',
            neededTime: '',
          });
          break;

        case 'AddLocationTime':
          state[language].locationSection.nearbyLocations[index].items[
            locationIndex
          ].neededTime = value;
          break;

        case 'AddLocationName':
          state[language].locationSection.nearbyLocations[index].items[
            locationIndex
          ].locationName = value;
          break;

        case 'RemoveMethod':
          state['ar'].locationSection.nearbyLocations.splice(index, 1);
          state['en'].locationSection.nearbyLocations.splice(index, 1);
          break;

        case 'RemoveLocation':
          state['ar'].locationSection.nearbyLocations[index].items.splice(
            locationIndex,
            1,
          );
          state['en'].locationSection.nearbyLocations[index].items.splice(
            locationIndex,
            1,
          );
          break;
        default:
          break;
      }
    },
    setCurrentTab: (state, action: PayloadAction<number>) => {
      state.currentTab = action.payload; // Update the current tab index
    },
  },
});

export const {
  setEditInvestmentData,
  clearEditInvestmentData,
  updateFieldValue,
  removeItemFromArray,
  addItemToArray,
  updateListValue,
  updateObjectArrayValue,
  handleNearbyLocations,
  setCurrentTab,
} = editInvestmentSlice.actions;

export const editInvestmentReducer = editInvestmentSlice.reducer;

export interface EditInvestmentState extends initialStateType {}
